import { Button, InputAdornment, Typography } from "@mui/material";
import { Box, Grid } from "@mui/system";
import { Autocomplete, TextField } from "@mui/material";
import { Chip } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectUserData } from "../../../../features/userSlice";
import {
  selectBizDashboardLoading,
  selectBizComparisonLoading,
} from "../../../../features/bizDevelopmentDashboardSlice";

interface SiteOption {
  shared_uid: string;
  site_name: string;
}

interface Props {
  onGetData: (siteSharedUids: string[]) => void;
  onClearSites: () => void;
}

const SELECT_ALL_OPTION: SiteOption = {
  shared_uid: "ALL",
  site_name: "Select All",
};

const BizDevelopmentDashboardSearch = ({ onGetData, onClearSites }: Props) => {
  const response = useSelector(selectUserData);
  const isLoading = useSelector(selectBizDashboardLoading);
  const isComparisonLoading = useSelector(selectBizComparisonLoading);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedWebsites, setSelectedWebsites] = useState<SiteOption[]>([]);

  const siteOptions = useMemo((): SiteOption[] => {
    return (
      response?.data?.website_permissions?.map((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0];
        const site_name = siteObj[shared_uid].ui_site_name;
        return { shared_uid, site_name };
      }) || []
    );
  }, [response]);

  // Restore selected sites from URL params once siteOptions are loaded
  useEffect(() => {
    if (siteOptions.length === 0) return;
    const sitesParam = searchParams.get("sites");
    if (!sitesParam) return;
    const uids = sitesParam.split(",").filter(Boolean);
    const matched = siteOptions.filter((s) => uids.includes(s.shared_uid));
    if (matched.length > 0) setSelectedWebsites(matched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteOptions]);

  const autocompleteOptions = useMemo((): SiteOption[] => {
    const allSelected =
      selectedWebsites.length === siteOptions.length && siteOptions.length > 0;
    return allSelected ? siteOptions : [SELECT_ALL_OPTION, ...siteOptions];
  }, [siteOptions, selectedWebsites]);

  const handleSiteChange = (_: any, newValue: SiteOption[]) => {
    if (newValue.some((v) => v.shared_uid === "ALL")) {
      setSelectedWebsites(siteOptions);
    } else {
      setSelectedWebsites(newValue);
      if (newValue.length === 0) {
        onClearSites();
      }
    }
  };

  const handleGetData = () => {
    const siteSharedUids = selectedWebsites.map((s) => s.shared_uid);
    setSearchParams((prev) => {
      prev.set("sites", siteSharedUids.join(","));
      return prev;
    });
    onGetData(siteSharedUids);
  };

  const isGetDataDisabled =
    selectedWebsites.length === 0 || isLoading || isComparisonLoading;

  return (
    <Box sx={{ padding: "10px 16px 16px" }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Grid size={12}>
          <Typography variant="h3" sx={{ fontSize: "24px" }}>
            Biz Development Dashboard
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "14px" }}>
            Monitor, analyze, and mitigate data irregularities quickly with an
            actionable task list.
          </Typography>
        </Grid>

        <Grid size={12} display="flex" gap={2} alignItems="center">
          <Autocomplete
            multiple
            size="small"
            fullWidth
            options={autocompleteOptions}
            value={selectedWebsites}
            onChange={handleSiteChange}
            getOptionLabel={(option) => option.site_name || ""}
            isOptionEqualToValue={(option, value) =>
              option.shared_uid === value.shared_uid
            }
            filterSelectedOptions
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.shared_uid}
                  label={option.site_name}
                  sx={{
                    backgroundColor: "#7B1FA2",
                    color: "#ffffff",
                    height: "27px",
                  }}
                  deleteIcon={
                    <CancelIcon
                      sx={{
                        color: "rgba(255, 255, 255, 0.8) !important",
                        fontSize: "16px !important",
                      }}
                    />
                  }
                />
              ))
            }
            renderOption={(props, option: SiteOption) => (
              <li
                {...props}
                key={option.shared_uid}
                style={{
                  color: option.shared_uid === "ALL" ? "#7B1FA2" : "inherit",
                  fontWeight: option.shared_uid === "ALL" ? 500 : 400,
                }}
              >
                {option.site_name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                data-tracker="797"
                placeholder="Search site name or choose from the list"
                label="Select Website"
                color="secondary"
                slotProps={{ inputLabel: { shrink: true } }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon sx={{ marginLeft: "12px" }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Button
            color="secondary"
            size="medium"
            variant="contained"
            disabled={isGetDataDisabled}
            onClick={handleGetData}
            sx={{ whiteSpace: "nowrap", px: 3 }}
            data-tracker="798"
          >
            Get Data
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BizDevelopmentDashboardSearch;
